import React, { useEffect, useRef } from "react";
import { Form, FormInstance, Input, Select, theme, Typography } from "antd";
import { KlearlyCountryAlpha2Code } from "../../../../../interfaces/klearlyCountryAlpha2Code.enum";
import CountryFlag from "../../../../common/country-flag/CountryFlag";
import { WizardMode } from "../wizardSteps";

const { Text } = Typography;

// Looser than IPartnerDetails so it accepts the wizard's in-progress draft
// (phone parts may be empty mid-edit).
type PartnerDetailsValues = {
  country?: KlearlyCountryAlpha2Code;
  name?: string;
  ownerName?: string;
  email?: string;
  phone?: { countryCode?: string; number?: string };
};

interface PartnerDetailsStepProps {
  form: FormInstance;
  mode: WizardMode;
  details?: PartnerDetailsValues;
  // Edit mode controls the read/edit toggle from the wizard so it persists
  // across tab switches; create is always editing.
  editing?: boolean;
  onValuesChange?: (
    changed: PartnerDetailsValues,
    all: PartnerDetailsValues
  ) => void;
}

const countryName = (alpha2?: string) => {
  if (!alpha2) return "";
  const entry = Object.entries(KlearlyCountryAlpha2Code).find(
    ([, code]) => code === alpha2
  );
  return entry ? entry[0] : alpha2.toUpperCase();
};

export default function PartnerDetailsStep({
  form,
  mode,
  details,
  editing,
  onValuesChange,
}: PartnerDetailsStepProps) {
  // Create is always editable; edit opens on a read summary the wizard expands.
  const formMounted = mode === "create" || !!editing;

  // Seed once per mount (and once when the read view first expands). Skipping
  // re-seeds keeps later draft updates from clobbering what the user is typing.
  const seededRef = useRef(false);
  useEffect(() => {
    if (!formMounted || seededRef.current) return;
    seededRef.current = true;
    form.setFieldsValue({
      country: details?.country,
      name: details?.name,
      ownerName: details?.ownerName,
      email: details?.email,
      phone: {
        countryCode: details?.phone?.countryCode ?? "31",
        number: details?.phone?.number,
      },
    });
  }, [details, form, formMounted]);

  if (mode === "edit" && !formMounted) {
    return <ReadView details={details} />;
  }

  return (
    <Form
      form={form}
      layout="vertical"
      requiredMark
      autoComplete="off"
      onValuesChange={onValuesChange}
    >
      <Form.Item
        name="country"
        label="Country"
        rules={[{ required: true, message: "Please select a country" }]}
      >
        <Select
          showSearch
          optionFilterProp="label"
          placeholder="Select a country"
          style={{ maxWidth: 280 }}
          options={Object.entries(KlearlyCountryAlpha2Code).map(
            ([name, code]) => ({
              value: code,
              label: name,
              key: code,
            })
          )}
          optionRender={(opt) => (
            <span>
              <CountryFlag
                alpha2={opt.data.value as string}
                style={{ marginRight: 8 }}
              />
              {opt.data.label}
            </span>
          )}
        />
      </Form.Item>

      <Form.Item
        name="name"
        label="Partner Name"
        rules={[{ required: true, message: "Please enter the partner name" }]}
      >
        <Input placeholder="e.g. Bistro Holding B.V." />
      </Form.Item>

      <Form.Item
        name="ownerName"
        label="Owner Name"
        rules={[{ required: true, message: "Please enter the owner name" }]}
      >
        <Input placeholder="e.g. Sven Bakker" />
      </Form.Item>

      <Form.Item
        name="email"
        label="Email"
        rules={[
          { required: true, message: "Please enter an email" },
          { type: "email", message: "Please enter a valid email" },
        ]}
      >
        <Input placeholder="name@company.com" />
      </Form.Item>

      <Form.Item
        label="Contact Phone Number"
        required
        style={{ marginBottom: 4 }}
      >
        <Input.Group compact>
          <Form.Item
            name={["phone", "countryCode"]}
            noStyle
            rules={[{ required: true, message: "Code" }]}
          >
            <Input
              addonBefore="+"
              style={{ width: 90 }}
              inputMode="numeric"
              placeholder="31"
            />
          </Form.Item>
          <Form.Item
            name={["phone", "number"]}
            noStyle
            rules={[{ required: true, message: "Please enter a phone number" }]}
          >
            <Input
              style={{ width: 220 }}
              inputMode="numeric"
              placeholder="6 12 34 56 78"
            />
          </Form.Item>
        </Input.Group>
      </Form.Item>
      <Text type="secondary" style={{ fontSize: 12 }}>
        Primary phone for the partner's main contact.
      </Text>
    </Form>
  );
}

function ReadView({ details }: { details?: PartnerDetailsValues }) {
  const { token } = theme.useToken();

  const rows: { label: string; value: React.ReactNode }[] = [
    {
      label: "Country",
      value: details?.country ? (
        <span>
          <CountryFlag alpha2={details.country} style={{ marginRight: 8 }} />
          {countryName(details.country)}
        </span>
      ) : (
        "-"
      ),
    },
    { label: "Partner name", value: details?.name || "-" },
    { label: "Owner name", value: details?.ownerName || "-" },
    { label: "Email", value: details?.email || "-" },
    {
      label: "Contact phone number",
      value: details?.phone?.number
        ? `+${details.phone.countryCode ?? ""} ${details.phone.number}`
        : "-",
    },
  ];

  return (
    <div>
      {rows.map((row, index) => (
        <div
          key={row.label}
          style={{
            display: "grid",
            gridTemplateColumns: "220px minmax(0, 1fr)",
            alignItems: "center",
            gap: 16,
            padding: "12px 0",
            borderBottom:
              index < rows.length - 1
                ? `1px solid ${token.colorBorderSecondary}`
                : "none",
          }}
        >
          <Text
            type="secondary"
            style={{
              fontSize: 12,
              textTransform: "uppercase",
              letterSpacing: 0.4,
            }}
          >
            {row.label}
          </Text>
          <span style={{ fontWeight: 500, textAlign: "left" }}>
            {row.value}
          </span>
        </div>
      ))}
    </div>
  );
}
